import React, { useEffect, useState } from 'react';
import { useNewUI } from '../hooks/useNewUI';
import { cn } from '../utils/classNames';
import { Button } from '../components/ui';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  Users, Zap, Globe, Award, Search, Filter,
  ArrowRight, Battery, Gauge, Scale, Star
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { MOTORCYCLE_MODELS } from '../constants/products';
import { formatCurrency } from '../utils/currency';

const Products: React.FC = () => {
  const isNewUI = useNewUI();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Produits | Proxy Emobility';
    setLoading(false);
  }, []);

  const stats = [
    {
      icon: <Users className="w-8 h-8 text-primary-600" />,
      value: "1000+",
      label: "Clients Satisfaits",
      description: "à travers l'Afrique"
    },
    {
      icon: <Zap className="w-8 h-8 text-primary-600" />,
      value: "3",
      label: "Modèles",
      description: "pour tous les besoins"
    },
    {
      icon: <Globe className="w-8 h-8 text-primary-600" />,
      value: "10",
      label: "Pays",
      description: "couverts"
    },
    {
      icon: <Award className="w-8 h-8 text-primary-600" />,
      value: "95%",
      label: "Satisfaction",
      description: "client"
    }
  ];

  return (
    <div className="pt-20">
      <Helmet>
        <title>Motos Électriques | Proxy Emobility</title>
        <meta name="description" content="Découvrez notre gamme de motos électriques Terra, Sunu et Teranga. Des solutions durables, performantes et adaptées au contexte africain pour tous vos besoins de mobilité." />
        <meta name="keywords" content="motos électriques, Terra, Sunu, Teranga, mobilité durable, Afrique, transport écologique, batterie électrique, autonomie, recharge" />
        <link rel="canonical" href="https://proxyemobility.netlify.app/products" />
        
        {/* Schema.org structured data for Product List */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "ItemList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "item": {
                    "@type": "Product",
                    "name": "Terra",
                    "description": "Moto électrique robuste pour tous les terrains avec une autonomie exceptionnelle",
                    "image": "https://images.pexels.com/photos/2393835/pexels-photo-2393835.jpeg",
                    "url": "https://proxyemobility.netlify.app/products/terra",
                    "brand": {
                      "@type": "Brand",
                      "name": "Proxy Emobility"
                    },
                    "offers": {
                      "@type": "Offer",
                      "price": "1750000",
                      "priceCurrency": "XOF",
                      "availability": "https://schema.org/InStock"
                    }
                  }
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "item": {
                    "@type": "Product",
                    "name": "Sunu",
                    "description": "Élégante et performante, parfaite pour la mobilité urbaine et les déplacements quotidiens",
                    "image": "https://images.pexels.com/photos/13035553/pexels-photo-13035553.jpeg",
                    "url": "https://proxyemobility.netlify.app/products/sunu",
                    "brand": {
                      "@type": "Brand",
                      "name": "Proxy Emobility"
                    },
                    "offers": {
                      "@type": "Offer",
                      "price": "1500000",
                      "priceCurrency": "XOF",
                      "availability": "https://schema.org/InStock"
                    }
                  }
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "item": {
                    "@type": "Product",
                    "name": "Teranga",
                    "description": "Le choix économique et fiable pour tous, accessible et facile à prendre en main",
                    "image": "https://images.pexels.com/photos/2611675/pexels-photo-2611675.jpeg",
                    "url": "https://proxyemobility.netlify.app/products/teranga",
                    "brand": {
                      "@type": "Brand",
                      "name": "Proxy Emobility"
                    },
                    "offers": {
                      "@type": "Offer",
                      "price": "800000",
                      "priceCurrency": "XOF",
                      "availability": "https://schema.org/InStock"
                    }
                  }
                }
              ]
            }
          `}
        </script>
      </Helmet>
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-800 to-primary-900 text-white py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,_rgba(255,255,255,0.1)_0%,_transparent_70%)]"></div>
        <div className="container mx-auto px-4 md:px-6 relative">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white via-gray-100 to-accent-200 bg-clip-text text-transparent">
              Nos Motos Électriques
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Découvrez notre gamme de motos électriques conçues pour répondre aux besoins de mobilité en Afrique. Performance, fiabilité et durabilité au service de votre mobilité.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center transform hover:scale-105 transition-all duration-300">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-50 rounded-full mb-4 shadow-md">
                  {stat.icon}
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                <div className="text-lg font-semibold text-primary-600 mb-1">{stat.label}</div>
                <div className="text-sm text-gray-600">{stat.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-16 bg-gradient-to-b from-white to-primary-50">
        <div className="container mx-auto px-4 md:px-6">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-2xl shadow-lg overflow-hidden animate-pulse">
                  <div className="h-64 bg-gray-200" />
                  <div className="p-8 space-y-4">
                    <div className="h-6 bg-gray-200 rounded w-3/4" />
                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                    <div className="h-20 bg-gray-200 rounded" />
                    <div className="grid grid-cols-3 gap-4">
                      {[1, 2, 3].map((j) => (
                        <div key={j} className="h-16 bg-gray-200 rounded" />
                      ))}
                    </div>
                    <div className="h-10 bg-gray-200 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {MOTORCYCLE_MODELS.map((model) => (
                <div key={model.id} className="group relative overflow-hidden rounded-2xl shadow-xl transition-all duration-500 hover:shadow-2xl">
                  {/* Background Gradient */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${model.color} opacity-5 group-hover:opacity-10 transition-opacity duration-500`}></div>
                  
                  {/* Image */}
                  <div className="relative h-64 md:h-72 overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
                    {model.image && (
                      <img
                        src={model.image}
                        alt={`Moto électrique ${model.name} - ${model.description}`}
                        loading="lazy"
                        width="400"
                        height="300"
                        className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-110 p-4"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22400%22 height=%22300%22%3E%3Crect fill=%22%23e5e7eb%22 width=%22400%22 height=%22300%22/%3E%3Ctext x=%2250%25%22 y=%2250%25%22 font-family=%22Arial%22 font-size=%2218%22 fill=%22%239ca3af%22 text-anchor=%22middle%22 dy=%22.3em%22%3E' + model.name + '%3C/text%3E%3C/svg%3E';
                        }}
                      />
                    )}

                    {/* Gradient overlay pour meilleure lisibilité */}
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/20 pointer-events-none"></div>
                    
                    {/* Top Section */}
                    <div className="absolute top-0 left-0 right-0 p-4 flex justify-between items-start">
                      {/* Usage Tag */}
                      <div className="px-3 py-1 rounded-full text-white font-medium text-sm shadow-md flex items-center gap-1.5 z-10">
                        <div className={`bg-gradient-to-r ${model.color} px-3 py-1 rounded-full flex items-center gap-1.5`}>
                          {model.usage}
                        </div>
                      </div>
                      
                      {/* Price Tag */}
                      <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-md">
                        <span className="font-bold text-primary-600">{formatCurrency(model.basePrice)}</span>
                      </div>
                    </div>
                    
                    {/* Bottom Section - Only visible on hover */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-t from-black/70 to-transparent">
                      <div className="flex flex-wrap gap-2 mb-3">
                        {model.highlights.map((highlight, idx) => (
                          <span key={idx} className="px-3 py-1 bg-white/80 backdrop-blur-sm text-primary-700 rounded-full text-sm font-medium">
                            {highlight}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-6 bg-white">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xl font-bold text-gray-900 group-hover:text-primary-600 transition-colors">{model.name}</h3>
                      <div className="flex items-center">
                        <Star size={18} className="text-yellow-400 fill-current" />
                        <span className="ml-1 text-gray-700 font-medium">{model.rating}</span>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 mb-6 line-clamp-2">{model.description}</p>
                    
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="flex flex-col items-center text-center">
                        <Battery className="text-primary-500 mb-2" size={20} />
                        <span className="text-sm font-medium text-gray-900">{model.specs.autonomie}</span>
                        <span className="text-xs text-gray-500">Autonomie</span>
                      </div>
                      <div className="flex flex-col items-center text-center">
                        <Gauge className="text-primary-500 mb-2" size={20} />
                        <span className="text-sm font-medium text-gray-900">{model.specs.vitesse}</span>
                        <span className="text-xs text-gray-500">Vitesse Max</span>
                      </div>
                      <div className="flex flex-col items-center text-center">
                        <Scale className="text-primary-500 mb-2" size={20} />
                        <span className="text-sm font-medium text-gray-900">{model.specs.poids}</span>
                        <span className="text-xs text-gray-500">Poids</span>
                      </div>
                    </div>

                    <Link
                      to={`/products/${model.id}`}
                      className={cn(
                        "w-full inline-flex items-center justify-center transition-all",
                        isNewUI
                          ? "ui-btn ui-btn-primary"
                          : "px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 shadow-md hover:shadow-lg hover:-translate-y-0.5"
                      )}
                    >
                      Voir les détails
                      <ArrowRight size={18} className="ml-2" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-primary-800 to-primary-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,_rgba(255,255,255,0.1)_0%,_transparent_70%)]"></div>
        <div className="container mx-auto px-4 md:px-6 relative">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 bg-gradient-to-r from-white via-gray-100 to-accent-200 bg-clip-text text-transparent">
              Prêt à Passer à l'Électrique ?
            </h2>
            <p className="text-xl text-gray-200 mb-8">
              Contactez-nous pour essayer une moto électrique ou obtenir plus d'informations sur nos produits et services.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact" 
                className="px-8 py-4 bg-accent-300 hover:bg-accent-400 text-primary-900 font-medium rounded-xl transition-colors shadow-lg hover:shadow-xl transform hover:translate-y-[-2px]"
              >
                Demander un essai
                <ArrowRight size={20} className="ml-2 inline-block" />
              </Link>
              <Link 
                to="/pay-as-you-go" 
                className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-all border border-white/30 transform hover:translate-y-[-2px]"
              >
                Simuler un financement
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;